import React, { useEffect, useState } from 'react'
import MessageThread from '../../components/MessageThread/MessageThread'
import MessagesList from '../../components/MessagesList/MessagesList'
import { Order, OrderMessageThread } from '../../model'
import { getMessageThreads } from '../../services/api/messages'
import { useUpdateEffect } from 'react-use'
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { getOrderDetailMessageThreads } from '../../services/api/orders'

const DynamicMessages: React.FunctionComponent<{
    canSearch?: boolean
}> = ({ canSearch = false }) => {
    const { orderId } = useParams()
    const context = useOutletContext<{ order?: Order }>()
    const { order: currentOrder = undefined } = context || {}
    const [selectedThread, setSelectedThread] = useState<number | null>(null)
    const [messageThreads, setMessageThreads] = useState<OrderMessageThread[]>([])
    const [searchString, setSearchString] = useState<string>('')
    const [searchParams] = useSearchParams()
    const [width, setWidth] = useState<number>(window.innerWidth)
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()
    const isMobile = width <= 1024

    const onOrderDetail = orderId !== undefined

    useEffect(() => {
        setLoading(true)
        fetchMessageThreads()

        window.addEventListener('resize', handleWindowSizeChange)
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useUpdateEffect(() => {
        setLoading(true)
        fetchMessageThreads()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchString])

    useEffect(() => {
        const selected = searchParams.get('selectedThread')
        if (selected) {
            setSelectedThread(Number(selected))
        } else {
            setSelectedThread(null)
        }
    }, [searchParams])

    const fetchMessageThreads = async () => {
        try {
            const params: any = { pageSize: 1000 }
            if (searchString) {
                params['searchString'] = searchString
            }

            let res
            if (onOrderDetail) {
                res = await getOrderDetailMessageThreads(parseInt(orderId), params)
            } else {
                res = await getMessageThreads(params)
            }

            if (res && res.items) {
                // Filter out messages that have no last message and then sort them by date/time
                const filteredMessages = res.items.filter((message: OrderMessageThread) => message.lastMessage)
                const sortedMessages = sortMessageThreads(filteredMessages)
                setMessageThreads(sortedMessages)

                if (!searchParams.get('selectedThread') && sortedMessages.length) {
                    navigate(`?selectedThread=${sortedMessages[0].id}`)
                }
            }
        } catch (e) {
            console.error(e)
        }
        setLoading(false)
    }

    const sortMessageThreads = (messages: OrderMessageThread[]) => {
        return messages.sort((a, b) => {
            return new Date(b.lastMessage?.date || 0).getTime() - new Date(a.lastMessage?.date || 0).getTime()
        })
    }

    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth)
    }

    return (
        <>
            {isMobile ? (
                <div className='flex-grow flex flex-col overflow-hidden rounded-xl'>
                    {selectedThread ? (
                        <MessageThread
                            threadId={selectedThread}
                            className={onOrderDetail ? 'border-l' : 'border-full'}
                        />
                    ) : (
                        <MessagesList
                            messageThreads={messageThreads}
                            selectedThread={selectedThread}
                            setSearchString={setSearchString}
                            loading={loading}
                            canSearch={canSearch}
                            onOrderDetail={onOrderDetail}
                            actionButtons={onOrderDetail ? ['startThread'] : []}
                            currentOrder={onOrderDetail && currentOrder ? currentOrder : undefined}
                        />
                    )}
                </div>
            ) : (
                <div className='flex flex-grow h-auto rounded-xl'>
                    <div className='w-1/3 flex flex-col flex-grow rounded-tl-xl rounded-bl-xl'>
                        <MessagesList
                            messageThreads={messageThreads}
                            selectedThread={selectedThread}
                            setSearchString={setSearchString}
                            loading={loading}
                            className={
                                onOrderDetail
                                    ? ''
                                    : 'border borderFadedPrimaryColor border-opacity-50 shadow-sm rounded-t-xl lg:rounded-tr-none rounded-b-xl lg:rounded-br-none border-r-0 '
                            }
                            canSearch={canSearch}
                            onOrderDetail={onOrderDetail}
                            actionButtons={onOrderDetail ? ['startThread'] : []}
                            currentOrder={onOrderDetail && currentOrder ? currentOrder : undefined}
                        />
                    </div>

                    <div className='w-2/3 flex flex-col rounded-tr-xl lg:rounded-tl-none rounded-br-xl'>
                        <MessageThread
                            threadId={selectedThread}
                            onSend={fetchMessageThreads}
                            className={onOrderDetail ? 'border-l' : 'border-full'}
                        />
                    </div>
                </div>
            )}
        </>
    )
}

export default DynamicMessages
